import { Badge, Card, cx, IconChevronRight, PageHeader, rowLinkClass } from '@timely/ui';
import { Link } from 'react-router-dom';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { useSetupStatus, type SetupItem, type SetupState } from '../onboarding/useSetupStatus';

/**
 * The full company setup status — every area, at once, with the same four
 * words for every one of them: Configured, Needs attention, Optional, Not
 * configured. Reopenable at any time from Settings, for a company whose
 * mandatory onboarding is long finished and just wants to check what it has
 * connected.
 */
const STATE_LABEL: Record<SetupState, string> = {
    configured: 'Configured',
    needs_attention: 'Needs attention',
    optional: 'Optional',
    not_configured: 'Not configured',
};

const STATE_TONE: Record<SetupState, 'pos' | 'warn' | 'neutral'> = {
    configured: 'pos', needs_attention: 'warn', optional: 'neutral', not_configured: 'neutral',
};
/** A required area that is not set up is the one thing to do first, so it is marked as needing attention. */
const toneOf = (item: SetupItem) => (item.required && item.state === 'not_configured' ? 'warn' : STATE_TONE[item.state]);

export function CompanySetup() {
    const context = useActiveWorkspace();
    const { items, loading } = useSetupStatus(context);
    const required = items.filter(i => i.required);
    const optional = items.filter(i => !i.required);

    return (
        <Page width="form">
            <PageHeader title="Setup checklist" subtitle="What is configured, what needs attention, and what is still optional." />

            <h2 className="text-secondary font-semibold text-label-3 mt-7 mb-2">Required before you start</h2>
            <Card className="divide-y divide-separator overflow-hidden">
                {required.map(item => <StatusRow key={item.area} item={item} loading={loading} />)}
            </Card>

            <h2 className="text-secondary font-semibold text-label-3 mt-8 mb-2">Optional</h2>
            <Card className="divide-y divide-separator overflow-hidden">
                {optional.map(item => <StatusRow key={item.area} item={item} loading={loading} />)}
            </Card>
        </Page>
    );
}

function StatusRow({ item, loading }: { item: SetupItem; loading: boolean }) {
    const opens = Boolean(item.can && item.href);
    return (
        // A row that can be opened is one link over the whole row, with a chevron, like the Settings list.
        <div className={cx('px-5 py-4 flex items-center justify-between gap-3', opens && 'relative hover:bg-hover')}>
            <div className="min-w-0">
                <div className="text-body font-semibold text-label">
                    {opens ? <Link to={item.href!} className={rowLinkClass}>{item.label}</Link> : item.label}
                </div>
                <div className="text-secondary text-label-3 mt-0.5 leading-snug">
                    {loading ? 'Checking…' : item.detail}
                </div>
            </div>
            {/* The state is words with the settle square, not a pill on every row; the chevron's place is kept on a
                row that does not open, so every state lines up in one column (2026-10 global sweep). */}
            <div className="flex items-center gap-3 flex-none">
                {!loading && <Badge variant="text" dot tone={toneOf(item)}>{STATE_LABEL[item.state]}</Badge>}
                <span className="w-3.5 text-label-4" aria-hidden="true">{opens && <IconChevronRight />}</span>
            </div>
        </div>
    );
}
